import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BusinessUnitModel } from '@fi-app/business-unit/business-unit.model';
import { UserModel } from '@fi-app/user/user.model';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AccessRightFilterCollectionModel, ILightWeightObject } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';
import { guid } from '@fi-shared/utils/guid';
import { DataFieldModel, getDataFieldsSaveData } from '@fi-app/data-field/data-field.model';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { first } from 'rxjs/operators';
import { ProfileEditComponent, ProfileImageAlignment } from '@fi-app/components/profile-edit/profile-edit.component';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { UserService } from '@fi-app/user/user.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { DataFieldPropertyType } from '@fi-app/data-field/data-field-property-type';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { BusinessAreaService } from '@fi-app/business-area/business-area.service';
import { Subject } from 'rxjs';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { NgModel } from '@angular/forms';
import { FiChipsSelectSearchHelperService } from '@fi-shared/fi-select-search/fi-chips-select-search/fi-chips-select-search-helper.service';
import { DataFieldHelperService } from '@fi-app/data-field/data-field-helper.service';
import { IUserContactUniqueData } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { FiUserSignInSettings } from '@fi-app/user/user-edit/fi-user-sign-in-settings';
import { FiCountryHelperService } from '@fi-shared/fi-contacts/fi-countries-select/country-helper.service';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { IContactModel } from '@fi-shared/models/common';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { UserAccessRequestsService } from '@fi-app/user/user-access-requests.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';

@Component({
    selector: 'fi-user-edit-new-layout',
    templateUrl: './user-edit-new-layout.component.html',
    styleUrls: ['./user-edit-new-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-edit-new-layout' },
    standalone: false,
})
export class UserEditNewLayoutComponent implements OnInit, OnDestroy {
    @Input() user: UserModel;
    @Input() availableBusinessUnits: ILightWeightObject[];
    @Input() lightWeightBusinessUnits: ILightWeightObject[];
    @Input() lightWeightBusinessAreas: ILightWeightBusinessAreaWithUnitId[];
    @Input() userDataFields: DataFieldModel[] = [];
    @Input() accessRightFilterCollection: AccessRightFilterCollectionModel;
    @Input() applicationLanguages: any[];
    @Input() contentLanguages: any[];
    @Input() userEmailUniqueData: IUserContactUniqueData[] = [];

    @ViewChild(ProfileEditComponent, { static: false }) profileEditComponent: ProfileEditComponent;

    ObjectTypes = ObjectTypes;
    ProfileImageAlignment = ProfileImageAlignment;
    DataFieldPropertyType = DataFieldPropertyType;
    updateBusinessAreas$ = new Subject<void>();
    initLightWeightRoles$ = new Subject<void>();

    isProfileMode: boolean = false;
    selectedTabIndex: number = 0;
    fiUserSignInSettings: FiUserSignInSettings;

    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private fiLoadingService: LoadingService,
        private userService: UserService,
        private dataFieldHelperService: DataFieldHelperService,
        private saveChangesWarningService: SaveChangesWarningService,
        private stateService: FiStateService,
        private stateChangeService: FiStateChangeService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private contentLanguagesService: ContentLanguagesService,
        private accessRightService: AccessRightService,
        private businessAreaService: BusinessAreaService,
        private stateAccessCheckService: StateAccessCheckService,
        private statisticService: StatisticService,
        private fiChipsSelectSearchHelperService: FiChipsSelectSearchHelperService,
        private injector: Injector,
        private fiCountryHelperService: FiCountryHelperService,
        private fiContactsService: FiContactsService,
        private userAccessRequestsService: UserAccessRequestsService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
    ) {}

    ngOnInit(): void {
        this.isProfileMode = this.user.Id && this.user.Id == this.userDataService.getUserId();
        this.user.checkAddDefaults(this.userDataService.applicationLanguage);

        this.userDataFields = this.userDataFields.filter((item) => {
            return (
                item.PropertyType !== DataFieldPropertyType.Areas /* && (this.isHasEditAccess() || (
                    item.PropertyType !== DataFieldPropertyType.Roles &&
                    item.PropertyType !== DataFieldPropertyType.Units &&
                    item.PropertyType !== DataFieldPropertyType.BusinessAreas &&
                    item.PropertyType !== DataFieldPropertyType.Tags)
            )*/
            );
        });

        this.saveChangesWarningService.start((isReceivingInitialObject) => {
            const obj: UserModel = cloneDeep(this.user);
            if (isReceivingInitialObject) obj.ApplicationLanguageType = this.user.ApplicationLanguageTypeOrig;

            return obj;
        });

        if (this.userDataService.userData) this.statisticService.Visit(ModuleType.Core, ObjectTypes.User, this.user.Id);

        if (this.user.IsAccessRequesting && this.user.Description) {
            this.fiDialogsService.custom({
                yesButtonText: this.appTextService.get('common.close'),
                headerText: this.appTextService.get('user.access_request_description_header'),
                messageText: this.user.Description,
                isNoButtonHidden: true,
            });
        }
        this.fiUserSignInSettings = new FiUserSignInSettings(this.user, this.injector);
    }

    checkPreferredBusinessUnit() {
        const businessUnits: BusinessUnitModel[] = this.GetLightWeightUserBusinessUnits();
        const preferredBusinessUnit: BusinessUnitModel = businessUnits.find((businessUnit: BusinessUnitModel) => {
            return businessUnit.Id === this.user.PreferredBusinessUnitId;
        });
        if (!preferredBusinessUnit?.IsActivated) {
            this.confirmDeactivatedUnitSaveAsPreferred();
        } else {
            this.Save();
        }
    }

    onValidateError(invalidModels: NgModel[]) {
        const rolesChipListModel = this.findInvalidModelByName(invalidModels, 'roles-chips-list');
        const unitChipListModel = this.findInvalidModelByName(invalidModels, 'units-chips-list');
        if (isDefined(rolesChipListModel) || isDefined(unitChipListModel)) {
            this.fiChipsSelectSearchHelperService.triggerChipsSelectErrorStateCheck();
        }
        this.showErrorDialog();
        if (invalidModels.some((model) => model.name?.includes('user-email'))) {
            this.selectedTabIndex = 0;
            return;
        }
        if (this.isWorkProfileTabError(invalidModels)) {
            this.selectedTabIndex = 1;
            return;
        }
    }

    private isWorkProfileTabError(invalidModels: NgModel[]): boolean {
        return invalidModels.some(
            (model) =>
                model.name?.includes('roles-chips-list') ||
                model.name?.includes('units-chips-list') ||
                model.name?.includes('unit-current-select') ||
                model.name?.includes('business-areas-select'),
        );
    }

    private findInvalidModelByName(invalidModels: NgModel[], modelName: string): NgModel {
        return invalidModels.find((model) => model.name?.includes(modelName));
    }

    private showErrorDialog() {
        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('common.warning'),
        });
    }

    protected GetLightWeightUserBusinessUnits(): BusinessUnitModel[] {
        const userBusinessUnits: BusinessUnitModel[] = [];
        this.user.UserToBusinessUnitIds.forEach((selectedUserToBusinessUnitId) => {
            const businessUnit = new BusinessUnitModel();
            businessUnit.Id = selectedUserToBusinessUnitId;
            const businessUnitObject = this.lightWeightBusinessUnits.find((item) => item.Id === businessUnit.Id);
            businessUnit.Name = businessUnitObject.Name;
            businessUnit.IsSystemObject = businessUnitObject.IsSystemObject;
            businessUnit.IsActivated = businessUnitObject.IsActivated;
            userBusinessUnits.push(businessUnit);
        });
        return userBusinessUnits;
    }

    private confirmDeactivatedUnitSaveAsPreferred() {
        const messageLabel: string =
            this.user.IsAccessRequesting || !this.user.Id
                ? 'error_message.noactiveunit'
                : 'error_message.noactiveunit_existing_user';

        this.fiDialogsService
            .warningConfirm({
                headerText: this.appTextService.get('user.save_deactivated_state_confirm'),
                messageText: this.appTextService.get(messageLabel),
            })
            .onYesButtonClick(() => this.Save());
    }

    Save() {
        if (this.isPasswordHiddenForNewUser()) this.user.Password = guid();

        this.removeLeadingZeroFromPhoneNumbers();
        this.removeSignInContactDuplicates();
        this.removeDefaultEmailDuplicates();
        this.removeUidPropertyFromContacts();

        this.profileEditComponent.profileItemOnSave().then(() => {
            this.user.DataFieldValues = getDataFieldsSaveData(this.userDataFields);

            const promise = this.fiLoadingService.trackPromise(this.userService.saveUser(this.user));

            promise.then(
                (data) => {
                    if (data) {
                        this.saveChangesWarningService.stop();
                        this.user.MediaBankPhysicalFileName = data.MediaBankPhysicalFileName;
                        this.user.MediaBankExtension = data.MediaBankExtension;
                        if (this.user.IsAccessRequesting) {
                            const notification = String.format(
                                this.appTextService.get('user_added'),
                                this.user.FullName,
                            );
                            this.fiDialogsService
                                .notify({
                                    headerText: this.appTextService.get('common.information'),
                                    messageText: notification,
                                })
                                .afterClosed(() => this.stateService.go(FiStateNames.User.List));
                        } else {
                            if (this.isProfileMode) {
                                if (!data.IsSystemObject) {
                                    const phoneNumberSignIn: IContactModel = data.PhoneNumberContacts.find(
                                        (phoneNumber: IContactModel) => {
                                            return phoneNumber.IsSignIn;
                                        },
                                    );
                                    if (!!phoneNumberSignIn) {
                                        this.updateUsersCountryCode(phoneNumberSignIn.CountryId);
                                        return;
                                    }
                                }
                                this.afterCurrentUserSaveReload();
                            } else {
                                this.stateService.go(FiStateNames.User.List);
                            }
                        }
                    } else {
                        this.fiDialogsService.error({
                            messageText: this.appTextService.get('error_message.default_header'),
                        });
                    }
                },
                () => {
                    if (this.isPasswordHiddenForNewUser()) this.user.Password = '';
                },
            );
        });
    }

    private removeLeadingZeroFromPhoneNumbers() {
        this.user.PhoneNumberContacts = this.user.PhoneNumberContacts.map((phoneNumberContact: IContactModel) => {
            if (phoneNumberContact.ContactValue.startsWith('0')) {
                phoneNumberContact.ContactValue = phoneNumberContact.ContactValue.substring(1);

                if (phoneNumberContact.IsSignIn) {
                    this.fiUserSignInSettings.updateUserLoginNameByContact(phoneNumberContact);
                }

                return phoneNumberContact;
            }
            return phoneNumberContact;
        });
    }

    private removeSignInContactDuplicates() {
        const getDuplicateContacts = (contacts: IContactModel[]) => {
            const signInContactIndex: number = contacts.findIndex(
                (emailContact: IContactModel) => emailContact.IsSignIn,
            );
            return contacts.filter((contact: IContactModel, index: number) => {
                const contactValue: string = this.fiContactsService.getContactValueForValidation(
                    contact.ContactValue,
                    contact.ExtendedValue,
                );
                const signInContactValue: string = this.fiContactsService.getContactValueForValidation(
                    this.fiUserSignInSettings.signInContact.ContactValue,
                    this.fiUserSignInSettings.signInContact.ExtendedValue,
                );

                return contactValue === signInContactValue && index !== signInContactIndex;
            });
        };

        if (this.fiUserSignInSettings.isEmailSignInType()) {
            const duplicateEmails: IContactModel[] = getDuplicateContacts(this.user.EmailAddressContacts);
            const isEmailDefault: boolean =
                duplicateEmails.some((email: IContactModel) => email.IsDefault) ||
                this.fiUserSignInSettings.signInContact.IsDefault;
            duplicateEmails.forEach((duplicateEmail: IContactModel) => {
                this.user.EmailAddressContacts.splice(this.user.EmailAddressContacts.indexOf(duplicateEmail), 1);
            });
            this.fiUserSignInSettings.signInContact.IsDefault = isEmailDefault;
        } else if (this.fiUserSignInSettings.isPhoneNumberSignInType()) {
            getDuplicateContacts(this.user.PhoneNumberContacts).forEach((duplicatePhone: IContactModel) => {
                this.user.PhoneNumberContacts.splice(this.user.PhoneNumberContacts.indexOf(duplicatePhone), 1);
            });
        }
    }

    private removeDefaultEmailDuplicates() {
        // https://chainformation.atlassian.net/browse/FI-5254
        // remove possible primary email duplicate
        const newPrimaryEmailContact = this.user.EmailAddressContacts.find((c) => c.IsDefault && !c.Id);
        if (newPrimaryEmailContact) {
            const existingEmailContacts = this.user.EmailAddressContacts.filter(
                (c) => c.ContactValue === newPrimaryEmailContact.ContactValue && c !== newPrimaryEmailContact,
            );
            existingEmailContacts.forEach((c) => {
                this.user.EmailAddressContacts.splice(this.user.EmailAddressContacts.indexOf(c), 1);
                newPrimaryEmailContact.ContactType = c.ContactType;
            });
        }
    }

    private removeUidPropertyFromContacts() {
        this.user.EmailAddressContacts = this.fiContactsService.removeUidFromContacts(this.user.EmailAddressContacts);
        this.user.PhoneNumberContacts = this.fiContactsService.removeUidFromContacts(this.user.PhoneNumberContacts);
    }

    private updateUsersCountryCode(countryId: number) {
        this.fiCountryHelperService.getCountryCodes().then((countries: ICountryModel[]) => {
            const signInCountry: ICountryModel = countries.find((country: ICountryModel) => country.Id === countryId);
            this.fiCountryHelperService.saveUserCountryModel(signInCountry);
            this.afterCurrentUserSaveReload();
        });
    }

    private afterCurrentUserSaveReload() {
        window.history.back();
        this.stateChangeService.onFinish$.pipe(first()).subscribe(() => location.reload());
    }

    isPasswordHiddenForNewUser(): boolean {
        return !this.user.Id && this.user.SendMagicLink;
    }

    getDataFieldsByPropertyType(propertyType: DataFieldPropertyType): DataFieldModel[] {
        return this.dataFieldHelperService.getDataFieldsByPropertyType(this.userDataFields, propertyType);
    }

    isADLogin() {
        return this.customerDataService.isADLogin();
    }

    onApplicationLanguageChange(newApplicationLanguageType: FiApplicationLanguageType) {
        if (this.userDataService.userData && this.user.Id === this.userDataService.getUserId()) {
            this.fiLoadingService.trackPromise(
                Promise.all([
                    this.getBusinessAreasByBusinessUnitIds(newApplicationLanguageType),
                    this.getAccessRightsForUser(newApplicationLanguageType),
                ]).then(() => this.processUpdateApplicationLanguage(newApplicationLanguageType)),
            );
        }
    }

    private getBusinessAreasByBusinessUnitIds(newApplicationLanguageType: FiApplicationLanguageType): Promise<void> {
        // https://chainformation.atlassian.net/browse/FI-3659
        // update business areas dropdown
        const lightWeightBusinessUnitIds = this.lightWeightBusinessUnits.map((item) => item.Id);
        return this.businessAreaService
            .GetBusinessAreasByBusinessUnitIds(lightWeightBusinessUnitIds, newApplicationLanguageType)
            .then((data) => {
                this.lightWeightBusinessAreas.forEach((businessArea) => {
                    const newBusinessArea = data.find((item) => item.Id === businessArea.Id);
                    if (newBusinessArea) businessArea.Name = newBusinessArea.Name;
                });
                this.updateBusinessAreas$.next();
            });
    }

    private getAccessRightsForUser(newApplicationLanguageType: FiApplicationLanguageType): Promise<void> {
        // update roles dropdown
        return this.accessRightService.GetAccessRightsForUser(newApplicationLanguageType).then((data) => {
            this.accessRightFilterCollection.AccessRightCoreFilter.Roles.forEach((role) => {
                const newRole = data.AccessRightCoreFilter.Roles.find((item) => item.Id === role.Id);
                if (newRole) role.Name = newRole.Name;
            });
            this.initLightWeightRoles$.next();
        });
    }

    // update dropdowns in user edit and request access component
    protected processUpdateApplicationLanguage(newApplicationLanguageType: FiApplicationLanguageType) {
        this.userService.updateApplicationLanguage(newApplicationLanguageType).then(() => {
            this.user.ApplicationLanguageType = newApplicationLanguageType;
            this.contentLanguagesService.UpdateContentLanguageNames(this.applicationLanguages);

            const applicationLanguages = this.contentLanguagesService.GetApplicationLanguages();
            this.contentLanguages.forEach((item) => {
                const newApplicationLanguage = applicationLanguages.find((lang) => lang.Id === item.Id);
                if (newApplicationLanguage) item.Name = newApplicationLanguage.Name;
            });
            const phoneNumberContacts = cloneDeep(this.user.PhoneNumberContacts);
            this.user.PhoneNumberContacts.length = 0;
            phoneNumberContacts.forEach((contact) => {
                this.user.PhoneNumberContacts.push(contact);
            });
        });
    }

    isHasEditAccess(): boolean {
        return (
            !this.user.Id ||
            this.user.Id === this.userDataService.getUserId() ||
            this.accessRightFilterCollection.AccessRightUserFilter.UserIds.indexOf(this.user.Id) > -1 ||
            (this.user.IsAccessRequesting &&
                this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.User.Edit))
        );
    }

    rejectUserRequest() {
        this.fiItemsDeleteHelperService.requestAccessDeleteConfirm([this.user]).then(() => {
            this.saveChangesWarningService.stop();
            this.userAccessRequestsService.rejectAccessRequest([this.user.Id]).then(() => {
                this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                    ObjectTypes.User,
                    [this.user.Id],
                    this.user.FullName,
                );
                window.history.back();
            });
        });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
